import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { drawCompanyBlock } from "@/lib/companyHeader";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { PageHeader, StatusBadge, StatCard, EmptyState, money, cur as curFmt, aed, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConsolidatedRegister } from "@/components/ConsolidatedRegister";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Receipt, CurrencyDollar, CheckCircle, Plus, Trash, MagnifyingGlass, ListNumbers } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import { DispatchInvoiceViewDialog } from "@/components/DispatchInvoiceView";
import { HeroUIProvider, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button as HButton } from "@heroui/react";

const STATUS_OPTIONS = ["issued", "partially_paid", "paid", "overdue", "cancelled"];
const FILTER_KEY = "invoices.filters";
const readFilters = () => { try { return JSON.parse(sessionStorage.getItem(FILTER_KEY)) || {}; } catch { return {}; } };
const emptyItem = () => ({ name: "", sku: "", quantity: 1, unit_price: 0 });

export default function Invoices() {
  const navigate = useNavigate();
  const { isTeam, user } = useAuth();
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const savedFilters = readFilters();
  const [filter, setFilter] = useState(() => savedFilters.filter || "all");
  const [search, setSearch] = useState(() => savedFilters.search || "");
  const [companyFilter, setCompanyFilter] = useState(() => savedFilters.companyFilter || "all");
  const [fromDate, setFromDate] = useState(() => savedFilters.fromDate || "");
  const [toDate, setToDate] = useState(() => savedFilters.toDate || "");
  useEffect(() => { sessionStorage.setItem(FILTER_KEY, JSON.stringify({ filter, search, companyFilter, fromDate, toDate })); }, [filter, search, companyFilter, fromDate, toDate]);

  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [shipping, setShipping] = useState(0);
  const [tax, setTax] = useState(0);
  const [dueDays, setDueDays] = useState(30);
  const [ptn, setPtn] = useState({ patient_name: "", postal_code: "", destination: "", cep: "", address: "" });
  const [busy, setBusy] = useState(false);
  const ptnBrazil = ptn.destination.trim().toLowerCase() === "brazil";
  const [orders, setOrders] = useState([]);
  const [cOpen, setCOpen] = useState(false);
  const [cOrderIds, setCOrderIds] = useState([]);
  const [cShip, setCShip] = useState(0);
  const [cHandling, setCHandling] = useState(0);
  const [cTax, setCTax] = useState(0);
  const [cDue, setCDue] = useState(30);
  const [cBusy, setCBusy] = useState(false);
  const cToggle = (id) => setCOrderIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const [source, setSource] = useState("team");
  const [dispatchRows, setDispatchRows] = useState([]);
  const [viewingDinv, setViewingDinv] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSel = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const isDispatchSrc = source === "dispatch";

  const loadDispatch = () => api.get("/dispatch-invoices").then((r) => setDispatchRows(r.data)).catch(() => {});
  const load = () => api.get("/invoices").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    if (isTeam) {
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      loadDispatch();
    }
  }, [isTeam]);
  useEffect(() => { setSelected([]); }, [source, filter, search]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/invoices/${id}/status`, { status });
      toast.success(`Invoice marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const removeInvoice = async (id, number) => {
    await api.delete(`/invoices/${id}`);
    toast.success(`Invoice ${number} deleted`);
    load();
  };

  const updateItem = (i, field, val) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const grandTotal = subtotal + (Number(shipping) || 0) + (subtotal + (Number(shipping) || 0)) * (Number(tax) || 0) / 100;

  const createInvoice = async () => {
    if (!clientId) return toast.error("Select a client");
    const valid = items.filter((it) => it.name.trim());
    if (valid.length === 0) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/manual", {
        client_id: clientId,
        items: valid.map((it) => ({ name: it.name, sku: it.sku, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 })),
        shipping_charge: Number(shipping) || 0,
        tax_rate: Number(tax) || 0,
        due_days: Number(dueDays) || 30,
        patient_name: ptn.patient_name || null,
        postal_code: ptn.postal_code || null,
        destination: ptn.destination || null,
        cep: ptnBrazil ? (ptn.cep || null) : null,
        address: ptn.address || null,
      });
      toast.success(`Invoice ${data.invoice_number} created`);
      setOpen(false);
      setClientId(""); setItems([emptyItem()]); setShipping(0); setTax(0); setDueDays(30);
      setPtn({ patient_name: "", postal_code: "", destination: "", cep: "", address: "" });
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const brazilApproved = orders.filter((o) => o.preferred_carrier === "brazil_courier" && ["approved", "shipped"].includes(o.status));
  const invoicedIds = new Set();
  rows.forEach((r) => { if (r.order_id) invoicedIds.add(r.order_id); (r.order_ids || []).forEach((x) => invoicedIds.add(x)); });
  const cSelClient = cOrderIds.length ? brazilApproved.find((o) => o.id === cOrderIds[0])?.client_id : null;
  const cSubtotal = brazilApproved.filter((o) => cOrderIds.includes(o.id)).reduce((s, o) => s + (o.items || []).reduce((a, it) => a + it.quantity * it.unit_price, 0), 0);
  const cTotal = cSubtotal + (Number(cShip) || 0) + (Number(cHandling) || 0) + (cSubtotal + (Number(cShip) || 0) + (Number(cHandling) || 0)) * (Number(cTax) || 0) / 100;

  const createConsolidatedInvoice = async () => {
    if (cOrderIds.length < 2) return toast.error("Select at least two Brazil Courier orders");
    if (cOrderIds.some((id) => invoicedIds.has(id))) {
      if (!window.confirm("One or more selected orders already have an invoice. Those older invoices will be removed and replaced by this new consolidated invoice. Continue?")) return;
    }
    setCBusy(true);
    try {
      const { data } = await api.post("/invoices/consolidated", {
        order_ids: cOrderIds, tax_rate: Number(cTax) || 0, shipping_charge: Number(cShip) || 0, handling_amount: Number(cHandling) || 0, due_days: Number(cDue) || 30,
      });
      const removed = data.superseded_invoices?.length || 0;
      toast.success(`Invoice ${data.invoice_number} created (${cOrderIds.length} orders)` + (removed ? ` · ${removed} old invoice${removed > 1 ? "s" : ""} removed` : ""));
      setCOpen(false); setCOrderIds([]); setCShip(0); setCHandling(0); setCTax(0); setCDue(30);
      navigate(`/invoices/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCBusy(false); }
  };

  const activeRows = isDispatchSrc ? dispatchRows : rows;
  const selRows = activeRows.filter((r) => selected.includes(r.id));
  const bulkTotal = selRows.reduce((s, r) => s + (isDispatchSrc ? (r.amount || 0) : (r.total || 0)), 0);
  const fmtCur = (n) => (isDispatchSrc ? aed(n) : money(n));

  const bulkMarkPaid = async () => {
    if (selRows.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(selRows.map((r) => (isDispatchSrc ? api.patch(`/dispatch-invoices/${r.id}/paid`) : api.patch(`/invoices/${r.id}/status`, { status: "paid" }))));
    const failed = results.filter((x) => x.status === "rejected").length;
    if (failed) toast.error(`${selRows.length - failed} of ${selRows.length} marked paid · ${failed} failed`);
    else toast.success(`${selRows.length} invoice(s) marked paid`);
    setSelected([]); setBulkBusy(false);
    load(); if (isTeam) loadDispatch();
  };

  const allSelectedPaid = selRows.length > 0 && selRows.every((r) => r.status === "paid");
  const paymentReceiptPdf = () => {
    const paidRows = selRows.filter((r) => r.status === "paid");
    if (paidRows.length === 0) {
      toast.error("Payment receipts can only be generated for paid invoices");
      return;
    }
    const paidTotal = paidRows.reduce((s, r) => s + (isDispatchSrc ? (r.amount || 0) : (r.total || 0)), 0);
    const doc = new jsPDF();
    const blue = [13, 35, 64], gold = [242, 183, 5];
    doc.setFillColor(...blue); doc.rect(0, 0, 210, 30, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    doc.text("HELIOPOLIS PHARMACEUTICALS", 14, 14);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Combined Payment Receipt · ${isDispatchSrc ? "Dispatch (AED)" : "Client (USD)"}`, 14, 21);
    doc.setFillColor(...gold); doc.rect(0, 30, 210, 1.5, "F");
    doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("Payment received — this receipt acknowledges the invoices below as paid.", 14, 38);
    let y = 48; doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("Invoice", 14, y); doc.text("Reference", 70, y); doc.text("Amount", 196, y, { align: "right" });
    doc.setDrawColor(220, 220, 220); doc.line(14, y + 2, 196, y + 2); doc.setFont("helvetica", "normal"); y += 8;
    paidRows.forEach((r) => {
      doc.text(String(r.invoice_number || r.dispatch_invoice_number), 14, y);
      doc.text(String((isDispatchSrc ? r.order_number : r.client_name) || "—").slice(0, 40), 70, y);
      doc.text(isDispatchSrc ? `AED ${Number(r.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : money(r.total), 196, y, { align: "right" });
      y += 7;
    });
    doc.setDrawColor(...gold); doc.setLineWidth(0.8); doc.line(14, y, 196, y); y += 10;
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...blue);
    doc.text(`Total received (${paidRows.length} invoices)`, 14, y);
    doc.text(isDispatchSrc ? `AED ${paidTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : money(paidTotal), 196, y, { align: "right" });
    doc.save(`payment-receipt-${Date.now()}.pdf`);
  };

  const isUnpaid = (i) => i.status !== "paid" && i.status !== "cancelled";

  const renderInvoicePage = (doc, inv, logo, defaultBank, blue, gray, line, company) => {
    const bank = inv.bank || defaultBank;
    const money = (n) => curFmt(n, inv.currency || bank.currency || "USD");
    const isConsol = (inv.order_numbers?.length || 0) > 1 || inv.consolidated;
    if (logo) { try { doc.addImage(logo, "PNG", 20, 14, 20, 20); } catch (e) { /* logo optional */ } }
    doc.setTextColor(...gray); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    const sepY = drawCompanyBlock(doc, company, gray);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...gray);
    doc.text("INVOICE", 190, 18, { align: "right" });
    doc.setTextColor(20, 20, 20); doc.setFontSize(17);
    doc.text(String(inv.invoice_number), 190, 27, { align: "right" });
    doc.setFontSize(8); doc.setTextColor(...blue);
    doc.text(String(inv.status || "").toUpperCase(), 190, 34, { align: "right" });
    doc.setDrawColor(...line); doc.setLineWidth(0.4); doc.line(20, sepY, 190, sepY);
    let y = sepY + 9;
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
    doc.text("BILL TO", 20, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(20, 20, 20);
    doc.text(String(inv.client_name || ""), 20, y + 7);
    doc.setFontSize(9); doc.setTextColor(...gray);
    doc.text(isConsol ? `Orders: ${(inv.order_numbers || []).join(", ")}` : `Order: ${inv.order_number}`, 20, y + 13);
    if (inv.client_reference) doc.text(`Reference: ${String(inv.client_reference).slice(0, 44)}`, 20, y + 19);
    doc.text(`Issued ${fmtDate(inv.issued_date)}`, 190, y, { align: "right" });
    doc.text(`Due ${fmtDate(inv.due_date)}`, 190, y + 6, { align: "right" });
    doc.setTextColor(20, 20, 20);
    y += 32;
    doc.setFont("helvetica", "bold");
    if (isConsol) { doc.text("Order", 20, y); doc.text("Product", 48, y); } else { doc.text("Product", 20, y); }
    doc.text("Qty", 120, y); doc.text("Unit", 145, y); doc.text("Amount", 175, y);
    doc.line(20, y + 2, 195, y + 2);
    doc.setFont("helvetica", "normal"); y += 8;
    (Array.isArray(inv.items) ? inv.items : []).forEach((it) => {
      if (y > 250) { doc.addPage(); y = 20; }
      if (isConsol) { doc.text(String(it.order_number || "—"), 20, y); doc.text(String(it.name).slice(0, 34), 48, y); }
      else { doc.text(String(it.name).slice(0, 50), 20, y); }
      doc.text(String(it.quantity), 120, y); doc.text(money(it.unit_price), 145, y); doc.text(money(it.quantity * it.unit_price), 175, y);
      y += 7;
    });
    doc.line(20, y, 195, y); y += 8;
    doc.text("Subtotal", 145, y); doc.text(money(inv.subtotal), 175, y);
    if (inv.shipping_charge > 0) { y += 7; doc.text("Shipping", 145, y); doc.text(money(inv.shipping_charge), 175, y); }
    if (inv.handling_total > 0) { y += 7; doc.text("Cool box handling", 145, y); doc.text(money(inv.handling_total), 175, y); }
    y += 7; doc.text(`Tax (${inv.tax_rate}%)`, 145, y); doc.text(money(inv.tax), 175, y);
    y += 7; doc.setFont("helvetica", "bold"); doc.text("Total", 145, y); doc.text(money(inv.total), 175, y);

    const pts = inv.patients || [];
    if (pts.length) {
      y += 14; doc.setDrawColor(...line); doc.line(20, y - 6, 190, y - 6);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text(`PATIENTS / DESTINATIONS (${pts.length})`, 20, y);
      doc.setFont("helvetica", "normal");
      pts.forEach((p) => {
        y += 9; doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(20, 20, 20);
        const nm = String(p.patient_name || "—"); doc.text(nm.slice(0, 46), 20, y);
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
        doc.text(String(p.order_number || ""), 190, y, { align: "right" });
        y += 5; doc.text(([p.destination_country, p.postal_code, p.cep, p.cpf, p.address].filter(Boolean).join(" · ") || "—").slice(0, 95), 20, y);
      });
    } else if (inv.patient_name || inv.destination_country || inv.address || inv.postal_code || inv.cpf) {
      y += 12; doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text("PATIENT / DESTINATION", 20, y);
      doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20); doc.setFontSize(9);
      [inv.patient_name && `Patient: ${inv.patient_name}`, inv.destination_country && `Destination: ${inv.destination_country}`, inv.postal_code && `Postal code: ${inv.postal_code}`, inv.cep && `CEP: ${inv.cep}`, inv.cpf && `CPF: ${inv.cpf}`, inv.address && `Address: ${inv.address}`].filter(Boolean).forEach((l) => { y += 6; doc.text(String(l).slice(0, 90), 20, y); });
    }

    y += 16; doc.setDrawColor(...line); doc.line(20, y - 6, 190, y - 6);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...blue);
    doc.text(`PAYMENT — BANK DETAILS (${(inv.currency || bank.currency || "USD").toUpperCase()} ACCOUNT)`, 20, y);
    doc.setFont("helvetica", "normal");
    const cell = (lab, val, x, yy) => { doc.setTextColor(...gray); doc.setFontSize(7.5); doc.text(lab, x, yy); doc.setTextColor(20, 20, 20); doc.setFontSize(9.5); doc.text(String(val || ""), x, yy + 5); };
    y += 9; cell("BANK", bank.bank_name, 20, y); cell("BRANCH", bank.branch, 85, y); cell("ACCOUNT TYPE", bank.account_type, 145, y);
    y += 14; cell("ACCOUNT NO", bank.account_no, 20, y); cell("IBAN", bank.iban, 85, y); cell("SWIFT", bank.swift, 145, y);
    y += 14; cell("REFERENCE", inv.invoice_number, 20, y);
  };

  const [scheduleAsk, setScheduleAsk] = useState(false);

  const combinedInvoicesPdf = async (includeSchedule = false) => {
    setScheduleAsk(false);
    if (selRows.length === 0) return;
    const clientNames = [...new Set(selRows.map((r) => r.client_name || "—"))];
    if (clientNames.length > 1) return toast.error("Select invoices for a single client to combine them into one PDF");
    const client = clientNames[0];
    const blue = [30, 127, 212], gray = [130, 130, 130], line = [225, 228, 232];
    let logo = null;
    try { logo = await fetch("/heliopolis-logo.png").then((b) => b.blob()).then((b) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); })); } catch (e) { /* logo optional */ }
    let bank = null, company = null;
    try { bank = (await api.get("/settings/bank")).data; } catch (e) { /* use default */ }
    try { company = (await api.get("/settings/company")).data; } catch (e) { /* optional */ }
    bank = bank || { bank_name: "Abu Dhabi Islamic Bank (ADIB)", branch: "Sheikh Zayed, UAE", account_type: "USD - Savings Account", account_no: "29345468", iban: "AE600500000000029345468", swift: "ABDIAEADXXX" };

    const doc = new jsPDF();
    selRows.forEach((inv, idx) => {
      if (idx > 0) doc.addPage();
      renderInvoicePage(doc, inv, logo, bank, blue, gray, line, company);
    });

    const navy = [13, 27, 42], gold = [245, 166, 35], soft = [240, 246, 253], zebra = [248, 250, 252], ink = [20, 20, 20], green = [24, 166, 106], amber = [245, 166, 35];
    const L = 20, R = 190, W = R - L;
    const sc = { inv: L + 3, issued: 72, status: 108, total: 152, due: R - 3 };
    const sHead = () => {
      if (logo) { try { doc.addImage(logo, "PNG", L, 12, 18, 18); } catch (e) { /* logo optional */ } }
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text("ACCOUNT STATEMENT", R, 16, { align: "right" });
      doc.setTextColor(...ink); doc.setFontSize(14);
      doc.text(String(client).slice(0, 40), R, 24, { align: "right" });
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text(`${selRows.length} invoice(s) · generated ${fmtDate(new Date().toISOString())} · ops@heliopolispharmaceuticals.com`, R, 30, { align: "right" });
      doc.setDrawColor(...gold); doc.setLineWidth(0.9); doc.line(L, 36, L + 22, 36);
      doc.setDrawColor(...line); doc.setLineWidth(0.3); doc.line(L + 22, 36, R, 36);
    };
    const sTable = (y) => {
      doc.setFillColor(...navy); doc.rect(L, y, W, 8, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(255, 255, 255);
      doc.text("INVOICE", sc.inv, y + 5.3); doc.text("ISSUED", sc.issued, y + 5.3); doc.text("STATUS", sc.status, y + 5.3);
      doc.text("TOTAL", sc.total, y + 5.3, { align: "right" }); doc.text("AMOUNT DUE", sc.due, y + 5.3, { align: "right" });
      return y + 8;
    };
    const sPage = () => { doc.addPage(); sHead(); return sTable(46); };
    let y = sPage();
    let billed = 0, paid = 0, due = 0;
    selRows.forEach((inv, i) => {
      if (y + 8 > 270) y = sPage();
      const t = Number(inv.total || 0); billed += t;
      if (inv.status === "paid") paid += t;
      const dueAmt = isUnpaid(inv) ? t : 0; due += dueAmt;
      if (i % 2 === 1) { doc.setFillColor(...zebra); doc.rect(L, y, W, 8, "F"); }
      doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...ink);
      doc.text(String(inv.invoice_number), sc.inv, y + 5.4);
      doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
      doc.text(fmtDate(inv.issued_date), sc.issued, y + 5.4);
      const st = String(inv.status || "").toUpperCase();
      const stColor = inv.status === "paid" ? green : (dueAmt ? amber : gray);
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...stColor);
      doc.text(st, sc.status, y + 5.4);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...ink);
      doc.text(money(t), sc.total, y + 5.4, { align: "right" });
      if (dueAmt) { doc.setFont("helvetica", "bold"); doc.setTextColor(...blue); doc.text(money(dueAmt), sc.due, y + 5.4, { align: "right" }); }
      else { doc.setTextColor(...gray); doc.text("—", sc.due, y + 5.4, { align: "right" }); }
      y += 8;
    });
    if (y + 40 > 270) y = sPage();
    y += 4;
    doc.setFillColor(...soft); doc.rect(L, y, W, 16, "F");
    doc.setDrawColor(...blue); doc.setLineWidth(1.2); doc.line(L, y, L, y + 16);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...gray);
    doc.text("TOTAL BILLED", L + 6, y + 6); doc.text("TOTAL PAID", L + 70, y + 6);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...ink);
    doc.text(money(billed), L + 6, y + 12.5); doc.setTextColor(...green); doc.text(money(paid), L + 70, y + 12.5);
    y += 20;
    doc.setFillColor(...navy); doc.rect(L, y, W, 22, "F");
    doc.setFillColor(...gold); doc.rect(L, y, 2.2, 22, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(200, 210, 225);
    doc.text("AMOUNT DUE · ISSUED & UNPAID", L + 8, y + 8);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
    doc.text(`${selRows.filter((r) => isUnpaid(r)).length} open invoice(s)`, L + 8, y + 15.5);
    doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(255, 255, 255);
    doc.text(money(due), R - 6, y + 14, { align: "right" });

    if (includeSchedule) renderSchedulePage(doc, client, logo, gray, line, blue);
    doc.save(`statement-${String(client).replace(/\s+/g, "_")}-${Date.now()}.pdf`);
  };

  const renderSchedulePage = (doc, client, logo, gray, line, blue) => {
    const navy = [13, 27, 42], gold = [245, 166, 35], soft = [240, 246, 253], zebra = [248, 250, 252], ink = [20, 20, 20];
    const L = 20, R = 190, W = R - L;
    const col = { ref: L + 3, item: 54, qty: 118, unit: 140, ship: 164, total: R - 3 };
    const pageHead = () => {
      if (logo) { try { doc.addImage(logo, "PNG", L, 12, 18, 18); } catch (e) { /* logo optional */ } }
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text("ITEMISED SCHEDULE", R, 16, { align: "right" });
      doc.setTextColor(...ink); doc.setFontSize(14);
      doc.text(String(client).slice(0, 40), R, 24, { align: "right" });
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text(`${selRows.length} invoice(s) · generated ${fmtDate(new Date().toISOString())}`, R, 30, { align: "right" });
      doc.setDrawColor(...gold); doc.setLineWidth(0.9); doc.line(L, 36, L + 22, 36);
      doc.setDrawColor(...line); doc.setLineWidth(0.3); doc.line(L + 22, 36, R, 36);
    };
    const tableHead = (y) => {
      doc.setFillColor(...navy); doc.rect(L, y, W, 8, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(255, 255, 255);
      doc.text("REFERENCE", col.ref, y + 5.3); doc.text("ITEM", col.item, y + 5.3);
      doc.text("QTY", col.qty, y + 5.3, { align: "right" }); doc.text("UNIT", col.unit, y + 5.3, { align: "right" });
      doc.text("SHIPPING", col.ship, y + 5.3, { align: "right" }); doc.text("TOTAL", col.total, y + 5.3, { align: "right" });
      return y + 8;
    };
    const newPage = () => { doc.addPage(); pageHead(); return tableHead(46); };
    let y = newPage();
    const ensure = (h) => { if (y + h > 270) y = newPage(); };
    let grand = 0, grandItems = 0, grandShip = 0;
    selRows.forEach((inv) => {
      const items = inv.items || [];
      const ship = Number(inv.shipping_charge || 0);
      const invTotal = Number(inv.total || 0);
      const itemsTotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
      grand += invTotal; grandItems += itemsTotal; grandShip += ship;
      const ref = inv.client_reference || "—";
      ensure(9 + 7);
      doc.setFillColor(...soft); doc.rect(L, y, W, 9, "F");
      doc.setDrawColor(...blue); doc.setLineWidth(1.2); doc.line(L, y, L, y + 9);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...ink);
      doc.text(String(ref).slice(0, 18), col.ref, y + 6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...gray);
      doc.text(`${inv.invoice_number}${inv.issued_date ? ` · issued ${fmtDate(inv.issued_date)}` : ""}${inv.patient_name ? ` · ${String(inv.patient_name).slice(0, 24)}` : ""}`, col.item + 8, y + 6);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...blue);
      doc.text(money(invTotal), col.total, y + 6, { align: "right" });
      y += 9;
      const rows = items.length ? items : [{ name: "No items recorded", quantity: "", unit_price: null }];
      rows.forEach((it, i) => {
        ensure(7);
        if (i % 2 === 1) { doc.setFillColor(...zebra); doc.rect(L, y, W, 7, "F"); }
        doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...ink);
        doc.text(String(it.name || "—").slice(0, 38), col.item, y + 4.8);
        doc.text(it.quantity !== "" ? String(it.quantity) : "", col.qty, y + 4.8, { align: "right" });
        doc.text(it.unit_price != null ? money(it.unit_price) : "", col.unit, y + 4.8, { align: "right" });
        if (it.unit_price != null && it.quantity !== "") { doc.setTextColor(...gray); doc.text(money(Number(it.quantity || 0) * Number(it.unit_price || 0)), col.total, y + 4.8, { align: "right" }); }
        y += 7;
      });
      ensure(8);
      doc.setDrawColor(...line); doc.setLineWidth(0.3); doc.line(col.item, y + 0.5, R, y + 0.5);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...gray);
      doc.text(`Items ${money(itemsTotal)}`, col.unit, y + 5.5, { align: "right" });
      doc.text(money(ship), col.ship, y + 5.5, { align: "right" });
      doc.setFont("helvetica", "bold"); doc.setTextColor(...ink); doc.text(money(invTotal), col.total, y + 5.5, { align: "right" });
      y += 11;
    });
    ensure(30);
    y += 2;
    doc.setFillColor(...navy); doc.rect(L, y, W, 22, "F");
    doc.setFillColor(...gold); doc.rect(L, y, 2.2, 22, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(200, 210, 225);
    doc.text(`GRAND TOTAL · ${selRows.length} INVOICE(S)`, L + 8, y + 8);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
    doc.text(`Items ${money(grandItems)}   ·   Shipping ${money(grandShip)}`, L + 8, y + 15.5);
    doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(255, 255, 255);
    doc.text(money(grand), R - 6, y + 14, { align: "right" });
  };

  const amt = (i) => (isDispatchSrc ? (i.amount || 0) : (i.total || 0));
  const total = activeRows.reduce((s, i) => s + amt(i), 0);
  const paid = activeRows.filter((i) => i.status === "paid").reduce((s, i) => s + amt(i), 0);
  const outstanding = activeRows.filter((i) => i.status !== "paid" && i.status !== "cancelled").reduce((s, i) => s + amt(i), 0);
  const companies = [...new Set(rows.map((r) => r.client_name).filter(Boolean))].sort();
  const q = search.trim().toLowerCase();
  const isOverdue = (i) => i.status === "overdue" || (i.status === "issued" && i.due_date && new Date(i.due_date) < new Date());
  const matchesTab = (i, tab) => tab === "all" || (tab === "overdue" ? isOverdue(i) : tab === "issued" ? i.status === "issued" && !isOverdue(i) : i.status === tab);
  const searchable = (i) => isDispatchSrc
    ? [i.dispatch_invoice_number, i.order_number, i.notes]
    : [i.invoice_number, i.order_number, i.client_name, i.client_reference, i.patient_name, ...(i.patients || []).map((p) => p.patient_name), ...(i.items || []).map((it) => it.name)];
  const invDate = (i) => (i.issued_date || i.created_at || "").slice(0, 10);
  const filtered = activeRows.filter((i) => {
    if (!matchesTab(i, filter)) return false;
    if (!isDispatchSrc && companyFilter !== "all" && i.client_name !== companyFilter) return false;
    if (q && !searchable(i).some((v) => String(v || "").toLowerCase().includes(q))) return false;
    const d = invDate(i);
    if (fromDate && (!d || d < fromDate)) return false;
    if (toDate && (!d || d > toDate)) return false;
    return true;
  });
  const tabCount = (tab) => activeRows.filter((i) => matchesTab(i, tab) && (isDispatchSrc || companyFilter === "all" || i.client_name === companyFilter)).length;
  const hasFilters = q || filter !== "all" || companyFilter !== "all" || fromDate || toDate;
  const clearFilters = () => { setSearch(""); setFilter("all"); setCompanyFilter("all"); setFromDate(""); setToDate(""); };
  const selClient = !isDispatchSrc && selRows.length ? selRows[0].client_name : null;
  const canSelect = (r) => isDispatchSrc || !selClient || r.client_name === selClient;
  const toggleInv = (r) => { if (!canSelect(r)) return toast.error("You can only select invoices for one client at a time"); toggleSel(r.id); };
  const anchorClient = selClient || (!isDispatchSrc ? filtered[0]?.client_name : null);
  const selGroup = isDispatchSrc ? filtered : filtered.filter((r) => r.client_name === anchorClient);
  const allSel = selGroup.length > 0 && selGroup.every((r) => selected.includes(r.id));
  const toggleAll = () => setSelected(allSel ? [] : selGroup.map((r) => r.id));
  const showSelect = isTeam || !isDispatchSrc;

  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  useEffect(() => { if (bodyRef.current) setScrollW(bodyRef.current.scrollWidth); }, [rows, isTeam, isDispatchSrc]);
  const syncTop = () => { if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft; };
  const syncBody = () => { if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft; };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Billing"
        title="Invoices"
        actions={isTeam && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCOpen(true)} data-testid="multi-order-invoice-button"><Plus size={16} className="mr-2" /> Multi-order (Brazil)</Button>
            <Button onClick={() => setOpen(true)} data-testid="new-invoice-button"><Plus size={16} className="mr-2" /> New Invoice</Button>
          </div>
        )}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {isTeam ? (
          <>
            <StatCard testid="inv-revenue" label="Revenue (Paid)" value={fmtCur(paid)} icon={CheckCircle} tone="green" />
            <StatCard testid="inv-outstanding" label="Outstanding" value={fmtCur(outstanding)} icon={Receipt} tone="red" />
            <StatCard testid="inv-total" label="Total Billed" value={fmtCur(total)} icon={CurrencyDollar} />
          </>
        ) : (
          <>
            <StatCard testid="inv-total" label={t("inv.statTotalSales")} value={money(total)} icon={CurrencyDollar} tone="green" />
            <StatCard testid="inv-outstanding" label={t("inv.statOutstanding")} value={money(outstanding)} icon={Receipt} tone="red" />
            <StatCard testid="inv-paid" label={t("inv.statPaid")} value={money(paid)} icon={CheckCircle} tone="green" />
          </>
        )}
      </div>

      {isTeam && (
        <div className="flex items-center gap-2 border border-border rounded-md bg-muted/40 p-1 w-fit" data-testid="invoice-source-toggle">
          <button onClick={() => setSource("team")} data-testid="source-team" className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${!isDispatchSrc ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Team invoices (USD)</button>
          <button onClick={() => setSource("dispatch")} data-testid="source-dispatch" className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${isDispatchSrc ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Dispatch invoices (AED)</button>
        </div>
      )}

      {showSelect && selRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border border-primary/30 bg-primary/5 rounded-md px-4 py-3" data-testid="bulk-action-bar">
          <span className="text-sm font-medium" data-testid="bulk-summary">{selRows.length} selected · Total {isTeam ? fmtCur(bulkTotal) : money(bulkTotal)}</span>
          <div className="flex-1" />
          {isTeam && <Button size="sm" className="rounded-sm" onClick={bulkMarkPaid} disabled={bulkBusy} data-testid="bulk-mark-paid">{bulkBusy ? "Working…" : "Mark selected paid"}</Button>}
          {isTeam && <Button size="sm" variant="outline" className="rounded-sm" onClick={paymentReceiptPdf} disabled={bulkBusy || !allSelectedPaid} title={allSelectedPaid ? "" : "Select only paid invoices to acknowledge payment"} data-testid="bulk-payment-receipt">Combined payment receipt (PDF)</Button>}
          {!isDispatchSrc && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => (selRows.length ? setScheduleAsk(true) : null)} disabled={bulkBusy} data-testid="bulk-combined-invoices">Download combined invoices (PDF)</Button>}
        </div>
      )}

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="invoice-filter-tabs">
          {[["all", t("inv.tabAll")], ["issued", t("inv.tabIssued")], ["paid", t("inv.tabPaid")], ["overdue", t("inv.tabOverdue")], ["cancelled", t("inv.tabCancelled")]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l} <span className="ml-1 text-xs tabular-nums opacity-60" data-testid={`tab-count-${v}`}>{tabCount(v)}</span></TabsTrigger>
          ))}
          {!isDispatchSrc && <TabsTrigger value="consolidated" data-testid="tab-consolidated">Consolidated</TabsTrigger>}
        </TabsList>
      </Tabs>

      {filter === "consolidated" ? <ConsolidatedRegister isTeam={isTeam} /> : (<>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 rounded-sm" placeholder={isDispatchSrc ? "Search by invoice / order…" : (isTeam ? "Search by product / item, invoice #, order #, client or PTN name…" : t("inv.searchPhClient"))} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="invoices-search" />
        </div>
        {!isDispatchSrc && (
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="rounded-sm w-full sm:w-56" data-testid="invoices-company-filter"><SelectValue placeholder="All companies" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All companies</SelectItem>
            {companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        )}
        <div className="flex items-center gap-1.5 flex-wrap w-full sm:w-auto" data-testid="invoices-date-filter">
          <span className="text-xs text-muted-foreground">From</span>
          <Input type="date" className="rounded-sm w-36 sm:w-40 flex-1 sm:flex-none" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} data-testid="invoices-date-from" />
          <span className="text-xs text-muted-foreground">To</span>
          <Input type="date" className="rounded-sm w-36 sm:w-40 flex-1 sm:flex-none" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} data-testid="invoices-date-to" />
        </div>
        {hasFilters && (
          <>
            <span className="text-xs text-muted-foreground" data-testid="invoices-filter-count">{filtered.length} of {activeRows.length}</span>
            <button type="button" onClick={clearFilters} className="text-xs text-primary hover:underline" data-testid="invoices-clear-filters">Clear filters</button>
          </>
        )}
      </div>

      {!loading && filtered.length === 0 ? (
        <EmptyState title={t("inv.none")} hint="Generate an invoice from an order, or create one on behalf of a client with 'New Invoice'." action={isTeam && <Button onClick={() => setOpen(true)}>New Invoice</Button>} />
      ) : (
        <div className="space-y-0">
          <div className="md:hidden space-y-3" data-testid="invoice-cards">
            {filtered.map((inv) => {
              const num = inv.invoice_number || inv.dispatch_invoice_number;
              return (
                <div key={inv.id} className={`hp-card p-4 space-y-2 ${!canSelect(inv) && showSelect ? "opacity-60" : ""}`} data-testid={`invoice-card-${num}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      {showSelect && <input type="checkbox" className="mt-1" checked={selected.includes(inv.id)} disabled={!canSelect(inv)} onChange={() => toggleInv(inv)} />}
                      <div className="min-w-0">
                        {isDispatchSrc ? <button onClick={() => setViewingDinv(inv)} className="font-heading font-bold text-primary text-left break-all" data-testid={`open-dispatch-invoice-card-${inv.dispatch_invoice_number}`}>{inv.dispatch_invoice_number}</button>
                          : <button onClick={() => navigate(`/invoices/${inv.id}`)} className="font-heading font-bold text-primary text-left break-all" data-testid={`open-invoice-card-${inv.invoice_number}`}>{inv.invoice_number}</button>}
                        <p className="text-xs text-muted-foreground">{inv.order_number}{!isDispatchSrc && inv.client_name && isTeam ? ` · ${inv.client_name}` : ""}{isDispatchSrc ? ` · ${inv.charge_type}` : ""}</p>
                      </div>
                    </div>
                    <p className="font-semibold tabular-nums shrink-0">{isDispatchSrc ? aed(inv.amount) : curFmt(inv.total, inv.currency)}</p>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>{t("inv.colIssued")} {fmtDate(inv.issued_date || inv.created_at)}{!isDispatchSrc && inv.due_date ? ` · ${t("inv.colDue")} ${fmtDate(inv.due_date)}` : ""}</span>
                    {isTeam && !isDispatchSrc ? (
                      <Select value={inv.status} onValueChange={(v) => setStatus(inv.id, v)}>
                        <SelectTrigger className="rounded-lg h-8 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_OPTIONS.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <StatusBadge status={isOverdue(inv) ? "overdue" : inv.status} />}
                  </div>
                  {isDispatchSrc && (
                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" size="sm" className="rounded-sm h-10 flex-1" onClick={() => setViewingDinv(inv)} data-testid={`view-dispatch-invoice-card-${inv.dispatch_invoice_number}`}><Receipt size={16} className="mr-2" /> View invoice</Button>
                    </div>
                  )}
                  {!isDispatchSrc && (
                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" size="sm" className="rounded-sm h-10 flex-1" onClick={() => navigate(`/invoices/${inv.id}`)} data-testid={`view-invoice-card-${inv.invoice_number}`}><Receipt size={16} className="mr-2" /> {t("inv.view", "View invoice")}</Button>
                      {isTeam && <ConfirmDelete testid={`delete-invoice-card-${inv.invoice_number}`} title={`Delete invoice ${inv.invoice_number}?`} description="This permanently removes the invoice record. This cannot be undone." onConfirm={() => removeInvoice(inv.id, inv.invoice_number)} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div ref={topRef} onScroll={syncTop} className="hidden md:block overflow-x-auto border border-b-0 border-border rounded-t-lg bg-card" data-testid="invoices-top-scroll"><div style={{ width: scrollW, height: 1 }} /></div>
          <div ref={bodyRef} onScroll={syncBody} className="hidden md:block border border-border rounded-b-lg overflow-x-auto bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                {showSelect && <th className="px-4 py-3 w-8"><input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="invoice-select-all" /></th>}
                <th className="px-4 py-3 font-semibold">{t("inv.colInvoice")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.order")}</th>
                <th className="px-4 py-3 font-semibold">{isDispatchSrc ? "Type" : t("common.client")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.total")}</th>
                <th className="px-4 py-3 font-semibold">{t("inv.colIssued")}</th>
                <th className="px-4 py-3 font-semibold">{t("inv.colDue")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.status")}</th>
                {isTeam && !isDispatchSrc && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv, i) => (
                <tr key={inv.id} className={`hover:bg-primary/5 transition-colors ${i % 2 ? "bg-muted/30" : ""} ${!canSelect(inv) ? "opacity-40" : ""}`} data-testid={`invoice-row-${inv.invoice_number || inv.dispatch_invoice_number}`}>
                  {showSelect && <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(inv.id)} disabled={!canSelect(inv)} onChange={() => toggleInv(inv)} data-testid={`invoice-select-${inv.invoice_number || inv.dispatch_invoice_number}`} /></td>}
                  <td className="px-4 py-3 font-medium">
                    {isDispatchSrc ? (
                      <button onClick={() => setViewingDinv(inv)} className="flex items-center gap-2 text-primary hover:underline" data-testid={`open-dispatch-invoice-${inv.dispatch_invoice_number}`}><Receipt size={16} />{inv.dispatch_invoice_number}</button>
                    ) : (
                      <button onClick={() => navigate(`/invoices/${inv.id}`)} className="flex items-center gap-2 text-primary hover:underline" data-testid={`open-invoice-${inv.invoice_number}`}>
                        <Receipt size={16} />{inv.invoice_number}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">{inv.order_number}</td>
                  <td className="px-4 py-3">{isDispatchSrc ? `${inv.charge_type} (${inv.mode === "air_cargo" ? "air" : "courier"})` : inv.client_name}</td>
                  <td className="px-4 py-3">{isDispatchSrc ? aed(inv.amount) : curFmt(inv.total, inv.currency)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.issued_date || inv.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{isDispatchSrc ? "—" : fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3">
                    {isTeam && !isDispatchSrc ? (
                      <Select value={inv.status} onValueChange={(v) => setStatus(inv.id, v)}>
                        <SelectTrigger className="rounded-lg h-8 w-32" data-testid={`invoice-status-${inv.invoice_number}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_OPTIONS.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <StatusBadge status={isOverdue(inv) ? "overdue" : inv.status} />
                    )}
                  </td>
                  {isTeam && !isDispatchSrc && (
                    <td className="px-4 py-3 text-right">
                      <ConfirmDelete
                        testid={`delete-invoice-${inv.invoice_number}`}
                        title={`Delete invoice ${inv.invoice_number}?`}
                        description="This permanently removes the invoice record. This cannot be undone."
                        onConfirm={() => removeInvoice(inv.id, inv.invoice_number)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      <HeroUIProvider>
        <Modal isOpen={scheduleAsk} onClose={() => setScheduleAsk(false)} placement="center" size="md" data-testid="schedule-ask-dialog">
          <ModalContent>
            <ModalHeader className="flex-col items-start gap-1 pb-2">
              <span className="flex items-center gap-2"><ListNumbers size={18} className="text-[#377DD3]" weight="bold" /> Add an itemised schedule?</span>
              <span className="text-xs font-normal text-muted-foreground">{selRows.length} selected invoice(s) · {selClient || ""}</span>
            </ModalHeader>
            <ModalBody className="gap-3 pb-2">
              <p className="text-sm text-muted-foreground">Appends a branded final page grouping every item under its invoice reference — qty, unit price and line totals — with each invoice's shipping and total, and a grand total across the selection.</p>
              <div className="rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-xs text-muted-foreground flex flex-wrap gap-x-5 gap-y-1">
                <span><strong className="text-foreground">{selRows.reduce((n, r) => n + (r.items || []).length, 0)}</strong> line items</span>
                <span>Shipping <strong className="text-foreground">{money(selRows.reduce((n, r) => n + Number(r.shipping_charge || 0), 0))}</strong></span>
                <span>Grand total <strong className="text-foreground">{money(selRows.reduce((n, r) => n + Number(r.total || 0), 0))}</strong></span>
              </div>
            </ModalBody>
            <ModalFooter className="gap-2">
              <HButton variant="bordered" radius="lg" onPress={() => combinedInvoicesPdf(false)} data-testid="schedule-skip">Download without schedule</HButton>
              <HButton radius="lg" className="bg-[#377DD3] text-white font-semibold" onPress={() => combinedInvoicesPdf(true)} data-testid="schedule-include">Include schedule</HButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </HeroUIProvider>
      </>)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Invoice (on behalf of client)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide">Client *</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1.5 rounded-lg" data-testid="manual-inv-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                <div className="col-span-5">Item</div><div className="col-span-2">SKU</div><div className="col-span-2">Qty</div><div className="col-span-2">Unit</div><div className="col-span-1" />
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                  <Input className="col-span-5 rounded-lg h-9" placeholder="Item" value={it.name} onChange={(e) => updateItem(i, "name", e.target.value)} data-testid={`manual-item-name-${i}`} />
                  <Input className="col-span-2 rounded-lg h-9" placeholder="SKU" value={it.sku} onChange={(e) => updateItem(i, "sku", e.target.value)} />
                  <Input type="number" className="col-span-2 rounded-lg h-9" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} data-testid={`manual-item-qty-${i}`} />
                  <Input type="number" step="0.01" className="col-span-2 rounded-lg h-9" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} data-testid={`manual-item-price-${i}`} />
                  <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => removeItem(i)} disabled={items.length === 1}><Trash size={16} /></button>
                </div>
              ))}
              <div className="px-3 py-2 border-t border-border">
                <Button variant="outline" size="sm" onClick={addItem} data-testid="manual-add-item"><Plus size={14} className="mr-1" /> Add line</Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wide">Shipping ($)</Label><Input type="number" step="0.01" className="mt-1.5 rounded-lg" value={shipping} onChange={(e) => setShipping(e.target.value)} data-testid="manual-shipping" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Tax (%)</Label><Input type="number" step="0.1" className="mt-1.5 rounded-lg" value={tax} onChange={(e) => setTax(e.target.value)} data-testid="manual-tax" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Due (days)</Label><Input type="number" className="mt-1.5 rounded-lg" value={dueDays} onChange={(e) => setDueDays(e.target.value)} data-testid="manual-due" /></div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Patient / destination details (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wide">PTN name</Label><Input className="mt-1.5 rounded-lg" value={ptn.patient_name} onChange={(e) => setPtn({ ...ptn, patient_name: e.target.value })} data-testid="manual-ptn-name" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Destination (country)</Label><Input className="mt-1.5 rounded-lg" value={ptn.destination} onChange={(e) => setPtn({ ...ptn, destination: e.target.value })} placeholder="e.g. Brazil" data-testid="manual-ptn-destination" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Postal code</Label><Input className="mt-1.5 rounded-lg" value={ptn.postal_code} onChange={(e) => setPtn({ ...ptn, postal_code: e.target.value })} data-testid="manual-ptn-postal" /></div>
                {ptnBrazil && <div data-testid="manual-ptn-cep-wrap"><Label className="text-xs uppercase tracking-wide">CEP (Brazil)</Label><Input className="mt-1.5 rounded-lg" value={ptn.cep} onChange={(e) => setPtn({ ...ptn, cep: e.target.value })} placeholder="00000-000" data-testid="manual-ptn-cep" /></div>}
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">PTN address</Label><Input className="mt-1.5 rounded-lg" value={ptn.address} onChange={(e) => setPtn({ ...ptn, address: e.target.value })} data-testid="manual-ptn-address" /></div>
              </div>
            </div>

            <div className="flex justify-between items-center border-t border-border pt-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Invoice Total</span>
              <span className="font-heading text-xl font-extrabold" data-testid="manual-total">{money(grandTotal)}</span>
            </div>
          </div>
          <DialogFooter><Button onClick={createInvoice} disabled={busy} data-testid="submit-manual-invoice">{busy ? "Creating…" : "Create Invoice"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Multi-order invoice (Brazil Courier)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide">Orders</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Tick 2+ approved or shipped Brazil Courier orders from the same client. One shipping + one handling charge applies to the whole invoice.</p>
              <div className="max-h-52 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                {brazilApproved.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">No Brazil Courier orders available.</p>}
                {brazilApproved.map((o) => {
                  const disabled = cSelClient && o.client_id !== cSelClient;
                  return (
                    <label key={o.id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${disabled ? "opacity-40" : "hover:bg-primary/5"}`} data-testid={`consol-inv-order-${o.order_number}`}>
                      <input type="checkbox" checked={cOrderIds.includes(o.id)} disabled={disabled} onChange={() => cToggle(o.id)} data-testid={`consol-inv-cb-${o.order_number}`} />
                      <span className="flex-1">{o.order_number} — {o.client_name}</span>
                      {o.status === "shipped" && <span className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Shipped</span>}
                      {invoicedIds.has(o.id) && <span className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">Invoiced</span>}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wide">Shipping ($)</Label><Input type="number" step="0.01" className="mt-1.5 rounded-sm" value={cShip} onChange={(e) => setCShip(e.target.value)} data-testid="consol-inv-shipping" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Handling ($)</Label><Input type="number" step="0.01" className="mt-1.5 rounded-sm" value={cHandling} onChange={(e) => setCHandling(e.target.value)} data-testid="consol-inv-handling" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Tax (%)</Label><Input type="number" step="0.1" className="mt-1.5 rounded-sm" value={cTax} onChange={(e) => setCTax(e.target.value)} data-testid="consol-inv-tax" /></div>
            </div>
            <div className="flex justify-between items-center border-t border-border pt-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Invoice Total</span>
              <span className="font-heading text-xl font-extrabold" data-testid="consol-inv-total">{money(cTotal)}</span>
            </div>
          </div>
          <DialogFooter><Button onClick={createConsolidatedInvoice} disabled={cBusy} data-testid="submit-consol-invoice">{cBusy ? "Creating…" : `Create invoice (${cOrderIds.length})`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <DispatchInvoiceViewDialog invoice={viewingDinv} onClose={() => setViewingDinv(null)} canPay={user?.role === "admin"} onPaid={async (v) => { try { await api.patch(`/dispatch-invoices/${v.id}/paid`); toast.success("Marked paid"); loadDispatch(); } catch (e) { toast.error("Failed"); } setViewingDinv(null); }} />
    </div>
  );
}
